"use client";
import { twMerge } from "tailwind-merge";
import type { DeviceState } from "../../types/DeviceStates";
import type { GridState } from "../../types/GridStates";
import { formatFieldValue } from "../../utils/formatting";
import type { FieldFormat } from "../../utils/formatting/types";
import { DeviceStateBadge, type DeviceStateBadgeProps } from "../DeviceStateBadge";
import { GridStateBadge } from "../GridStateBadge";
import { Skeleton } from "../Skeleton";

export interface DeviceStateWithMetricProps extends Pick<DeviceStateBadgeProps, "state" | "size" | "shape" | "icon"> {
  /** Custom label for the state badge */
  stateLabel?: string;
  /** The metric value to display */
  metric?: number | null;
  /** Formatter for the metric (uses FieldFormat system) */
  metricFormatter?: FieldFormat;
  /** Optional secondary state badge (e.g., grid status) */
  secondaryState?: DeviceState | GridState;
  /** Layout orientation */
  layout?: "horizontal" | "vertical";
  /** Loading state */
  isLoading?: boolean;
  /** Fixed width for column alignment (useful in tables) */
  fixedWidth?: boolean;
  /** Show icon only for secondary grid state (compact mode) */
  secondaryStateIconOnly?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * DeviceStateWithMetric
 *
 * Combines a device state badge with a formatted metric display.
 * Uses the existing FieldFormat system for flexible metric formatting.
 * Supports horizontal layout (for tables) and vertical layout (for cards/detail views).
 *
 * @example
 * ```tsx
 * // Battery: "Charging | 75%"
 * <DeviceStateWithMetric
 *   state="charging"
 *   metric={75}
 *   metricFormatter={{ type: "number", decimals: 0, suffix: "%" }}
 * />
 *
 * // Inverter: "On | 5.2 kW"
 * <DeviceStateWithMetric
 *   state="on"
 *   metric={5200}
 *   metricFormatter={{ type: "power", unit: "kW" }}
 * />
 *
 * // Thermostat: "Cool | 72°F"
 * <DeviceStateWithMetric
 *   state="cool"
 *   metric={72}
 *   metricFormatter={{ type: "temperature", unit: "F" }}
 * />
 * ```
 */
export function DeviceStateWithMetric({
  state,
  stateLabel,
  metric,
  metricFormatter,
  secondaryState,
  layout = "horizontal",
  isLoading,
  size = "sm",
  shape = "pill",
  icon,
  fixedWidth,
  secondaryStateIconOnly = false,
  className,
}: DeviceStateWithMetricProps) {
  if (isLoading) {
    return <Skeleton className={twMerge("inline-flex h-6 w-32", className)} />;
  }

  const formattedMetric = metric != null && metricFormatter ? formatFieldValue(metric, metricFormatter) : null;

  const isHorizontal = layout === "horizontal";

  // Show placeholder when formatter exists but no metric, to maintain alignment
  const showMetricPlaceholder = metricFormatter && !formattedMetric;

  return (
    <div className={twMerge("inline-flex items-center", isHorizontal ? "gap-2" : "flex-col gap-1", className)}>
      {/* Metric comes first (on the left), or placeholder for alignment */}
      {(formattedMetric || showMetricPlaceholder) && (
        <span
          className={twMerge(
            "font-medium inline-block",
            formattedMetric ? "text-text-body" : "text-text-caption",
            size === "sm" && "text-xs min-w-12",
            size === "md" && "text-sm min-w-14",
            size === "lg" && "text-base min-w-16"
          )}
        >
          {formattedMetric || "—"}
        </span>
      )}

      {/* Primary state badge */}
      <DeviceStateBadge
        state={state}
        label={stateLabel}
        icon={icon}
        size={size}
        shape={shape}
        fixedWidth={fixedWidth}
      />

      {/* Secondary state (grid status) on the right */}
      {secondaryState && (
        <>
          {/* Determine if secondary state is a GridState */}
          {secondaryState === "importing" || secondaryState === "exporting" ? (
            <GridStateBadge
              state={secondaryState as GridState}
              size={size}
              shape={shape}
              fixedWidth={fixedWidth}
              showIcon={secondaryStateIconOnly}
            />
          ) : (
            <DeviceStateBadge state={secondaryState as DeviceState} size={size} shape={shape} fixedWidth={fixedWidth} />
          )}
        </>
      )}
    </div>
  );
}
